<template>
  <v-tabs
    v-show="tabs.length !== 1 || !hideWhenOnlyOne"
    ref="tabs"
    v-model="currentTab"
    v-bind="$attrs"
    :dark="dark"
    :background-color="backgroundColor"
    :color="color || (!dark ? 'secondary' : 'primary')"
    :slider-color="sliderColor || (dark ? 'primary' : 'secondary')"
    show-arrows
    @change="tabChanged"
  >
    <core-tab
      v-for="(tab, index) in tabs"
      :key="index"
      class="px-1"
      :tab="tab"
      :index="index"
      :label-key="labelKey"
      :new-label-locale-key="newLabelLocaleKey"
      :first-fixed="firstFixed"
      @updated="tabUpdated"
      @close="closeTab(index, tab.entity._id)"
    />

    <slot />

    <v-dialog v-model="closeDialog" max-width="400px">
      <v-card>
        <v-card-title>
          <span class="headline">{{ $t('tab.modified.title') }}</span>
        </v-card-title>

        <v-card-text>
          {{ $t('tab.modified.text') }}
        </v-card-text>

        <v-card-actions>
          <div class="flex-grow-1" />
          <v-btn
            color="error"
            text
            @click="closeDialog = false"
          >
            {{ $t('common.no') }}
          </v-btn>

          <v-btn
            color="success"
            text
            @click="() => {
              closeDialog = false;
              removeTab(closeDialogIdx, closeDialogId);
            }"
          >
            {{ $t('common.yes') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-tabs>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

export default {
  props: {
    value: Number,
    firstFixed: Boolean,
    tabs: Array,
    tabIcon: String,
    labelKey: [String, Array, Function],
    newLabelLocaleKey: { type: String, default: 'common.new' },
    dark: Boolean,
    firstTabLocaleKey: String,
    color: { type: String },
    sliderColor: { type: String },
    backgroundColor: { type: String, default: '#eee' },
    hideWhenOnlyOne: Boolean,
  },

  data: () => ({
    currentTab: 0,
    closeDialog: false,
    closeDialogIdx: 0,
    closeDialogId: undefined,
  }),

  computed: {
    ...mapGetters('tabs', { isTabModified: 'isModified' }),
  },

  watch: {
    /**
     * Updates all tabs indexes.
     */
    tabs(newValue) {
      // Reorder the idx's when tabs changes
      let x = 0;
      for (const t of newValue) {
        t.idx = x++;
        t.tabs = newValue;
      }
    },

    value(newValue) {
      this.currentTab = newValue;
    },

    '$i18n.locale': function languageChanged() {
      this.tabs[0].label = this.$t(this.firstTabLocaleKey);
    },

    firstTabLocaleKey(newValue) {
      this.tabs[0].label = this.$t(newValue);
    },
  },

  methods: {
    ...mapActions('tabs', {
      setTabParent: 'setTab',
      clearTabParent: 'clearTabAndChildren',
    }),

    tabModified(idx) {
      const tab = _.get(this, ['tabs', idx]);
      return this.isTabModified(tab._id);
    },

    tabUpdated() {
      setTimeout(() => this.$refs.tabs.callSlider(), 100);
    },

    /**
     * When tab changes.
     *
     * @param {number} idx Index of new tab.
     */
    tabChanged(idx) {
      const tab = _.get(this, ['tabs', idx]);
      const { _id, entity, parentId } = tab;
      this.$emit('input', idx);
      this.$emit('change', idx, _id, entity, parentId);
    },

    /**
     * Find a tab by its index or entity id.
     *
     * @param {number} [idx] Index of tab.
     * @param {string} [entityId] Id of entity in tab.
     *
     * @returns {object}
     */
    findTab(idx, entityId) {
      return entityId !== undefined
        ? _.findIndex(this.tabs, tab => _.get(tab, 'entity._id') === entityId)
        : idx;
    },

    /**
     * Initiate a tab closing by its index or entity id. If modifications
     * are detected in the tab, a dialog will appear else removeTab will
     * be called directly.
     *
     * @param {number} [idx] Index of tab.
     * @param {string} [entityId] Id of entity in tab.
     */
    closeTab(idx, entityId) {
      const i = this.findTab(idx, entityId);
      if (i > 0 && this.tabModified(i)) {
        this.closeDialogIdx = idx;
        this.closeDialogId = entityId;
        this.closeDialog = true;
      } else {
        this.removeTab(idx, entityId);
      }
    },

    /**
     * Creates a tab.
     *
     * @param {BaseModel} entity Service model instance to associate with the tab.
     * @param {string} [label] Label for the tab.
     * @param {object} [args] Optional arguments to include in tab object.
     * @param {string} [parentId] Parent tab unique id.
     */
    createTab(entity, label, args, parentId) {
      const idx = this.tabs.length;
      const t = {
        _id: _.uniqueId('tab_'),
        idx,
        icon: this.tabIcon,
        label,
        entity,
        ...args,
      };
      this.tabs.push(t);
      this.setTabParent({
        _id: t._id,
        parentId,
        entityId: _.get(entity, '_id'),
        modified: false,
      });
      return t;
    },

    /**
     * Creates a new tab linked to an service model instance.
     *
     * @param {BaseMode} entity Service model instance to associate tab with.
     * @param {string} [label] Optional hard coded label.
     * @param {object} [args] Optional arguments to include in tab object.
     * @param {string} [parentId] Parent tab unique id.
     */
    newTab(entity, label, args, parentId) {
      const t = this.createTab(entity, label, args, parentId);
      const { idx } = t;
      this.currentTab = idx;
      this.tabChanged(idx);
      this.$emit('create', idx, _.get(entity, '_id'), entity, parentId);
      return t;
    },

    /**
     * Opens a new tab linked with an entity.
     *
     * @param {BaseModel} entity Service model instance to associate with the tab.
     * @param {object} [args] Optional arguments to include in tab object.
     * @param {string} [parentId] Parent tab unique id.
     */
    openTab(entity, args, parentId) {
      const entityId = entity._id;
      let idx = this.findTab(undefined, entityId);
      let t;
      if (idx === -1) {
        t = this.createTab(entity, undefined, args, parentId);
        idx = t.idx; // eslint-disable-line prefer-destructuring
      } else {
        t = _.get(this, ['tabs', idx]);
      }
      this.currentTab = idx;
      this.tabChanged(idx);
      this.$emit('open', idx, entityId, entity, parentId);
      return t;
    },

    /**
     * Removes a tab by its index or entity id.
     *
     * @param {number} [idx] Index of tab.
     * @param {string} [entityId] Id of entity in tab.
     */
    removeTab(idx, entityId) {
      const i = this.findTab(idx, entityId);
      if (i > 0) {
        const { _id, parentId } = this.tabs[i];
        this.tabs.splice(i, 1);
        this.clearTabParent(_id);
        this.currentTab = 0;
        this.tabChanged(0);
        this.$emit('close', idx, _id, entityId, parentId);
      }
    },

    closeAll() {
      while (this.tabs.length > 1) {
        this.removeTab(1);
      }
    },
  }
};
</script>
